<template>
  <div class="step-2 mt-6" v-if="steps[2]">
    <div class="mb-1">
      <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t("message.patternLength") }}</label>
      <div class="grid grid-cols-7 rounded-xl gap-2">
        <template v-for="patternValue in patternsList" v-bind:key="patternValue">
          <template v-if="patternValue * 1 <= 11">
            <div>
              <input type="radio" :id="'patternlen-' + (patternValue + 1)" name="brt-patternlen"
                :value="patternValue + 1" class="hidden peer" :checked="patternLenSelected === patternValue + 1"
                @change="$emit('update:patternLenSelected', patternValue + 1); $emit('pattern-click')">
              <label :for="'patternlen-' + (patternValue + 1)" :class="[
                'inline-flex justify-center items-center py-2 min-w-10 text-sm text-white font-medium border border-gray-300 rounded-lg focus:ring-4 focus:ring-blue-300',
                patternLenSelected === patternValue + 1
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-800 hover:bg-gray-100 dark:bg-white dark:text-black dark:hover:bg-gray-600 dark:hover:border-gray-600'
              ]">
                {{ patternValue + 1 }}
              </label>
            </div>
          </template>
        </template>
      </div>
    </div>

    <!-- Pattern information -->
    <div v-if="patternSymbols != '' && steps[3]" class="mb-1">
      <div class="p-3 bg-gray-50 rounded-md font-mono">
        <span class="text-sm font-medium text-gray-700 mb-2">{{ $t("message.pattern") }}:</span>
        {{ patternSymbols }}
        <button @click="$emit('open-matrix-modal')" class="text-blue-600 hover:text-blue-800 text-sm ml-2">
          {{ $t("message.seeAll") }}
        </button>
      </div>
    </div>

    <!-- Sector Length (conditional) -->
    <div class="mb-4" v-if="showSectorLength">
      <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t("message.sectorLength") }}</label>
      <input type="number"
        class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        id="sectorLength" aria-describedby="sectorLengthHelp" :value="sectorLength" @input="$emit('update:sectorLength', $event.target.value)" min="4" max="16" />
      <p id="sectorLengthHelp" class="mt-1 text-sm text-gray-500">{{ $t("message.sectorLengthHelp") }}</p>
    </div>

    <!-- Palette Selection -->
    <div class="mb-4">
      <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t("message.paletteSelect") }}</label>
      <div class="grid grid-cols-3 gap-2 rounded-xl">
        <div v-for="value in [9, 12, 13]" :key="value">
          <input type="radio" :id="'btr-pal' + value" name="btr-palette" :value="value" class="hidden peer"
            :checked="paletteSelected === value" @change="$emit('update:paletteSelected', value); $emit('pattern-click')" autocomplete="off" />
          <label :for="'btr-pal' + value" :class="[
            'inline-flex justify-center items-center py-2 min-w-10 text-sm font-medium border border-gray-300 rounded-lg focus:ring-4 focus:ring-blue-300',
            paletteSelected === value
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
          ]">{{ value }}</label>
        </div>
      </div>

      <!-- Palette Legend -->
      <div class="mt-3">
        <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t("message.paletteLegend") }}</label>
        <div class="overflow-hidden rounded-lg border border-gray-200">
          <table class="w-full" style="table-layout: fixed; text-align: center">
            <tbody>
              <tr>
                <td v-for="color in paletteRendered[1]" v-bind:key="color.id" v-bind:style="{
                  backgroundColor: color.hex,
                  width: '35px',
                  height: '30px'
                }">
                  <span class="text-xs font-semibold"
                    :style="{ color: isColorDark(color.hex) ? 'white' : 'black' }">{{ color.id }}</span>
                </td>
              </tr>
              <tr>
                <td v-for="color in paletteRendered[2]" v-bind:key="color.id" v-bind:style="{
                  backgroundColor: color.hex,
                  width: '35px',
                  height: '30px'
                }">
                  <span class="text-xs font-semibold"
                    :style="{ color: isColorDark(color.hex) ? 'white' : 'black' }">{{ color.id }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- View Options Section -->
    <div class="mb-4">
      <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t("message.viewMode") }}</label>
      <div class="inline-flex rounded-lg border border-gray-300 p-1 bg-gray-50" role="group">
        <div class="relative">
          <input
            type="radio"
            class="absolute w-full h-full opacity-0 z-10 cursor-pointer peer"
            id="btnradio1"
            value="colorized"
            :checked="mandalaForm === 'colorized'"
            @change="$emit('update:mandalaForm', 'colorized')"
            name="btnradio"
            autocomplete="off"
          />
          <label
            for="btnradio1"
            class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 cursor-pointer"
            :class="mandalaForm === 'colorized' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-200'"
          >
            {{ $t("message.colorizedCaption") }}
          </label>
        </div>

        <div class="relative">
          <input
            type="radio"
            class="absolute w-full h-full opacity-0 z-10 cursor-pointer peer"
            id="btnradio2"
            value="digits"
            :checked="mandalaForm === 'digits'"
            @change="$emit('update:mandalaForm', 'digits')"
            name="btnradio"
            autocomplete="off"
          />
          <label
            for="btnradio2"
            class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 cursor-pointer"
            :class="mandalaForm === 'digits' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-200'"
          >
            {{ $t("message.digitsCaption") }}
          </label>
        </div>

        <div class="relative" v-if="steps[3]">
          <input
            type="radio"
            class="absolute w-full h-full opacity-0 z-10 cursor-pointer peer"
            id="btnradio3"
            value="dots"
            :checked="mandalaForm === 'dots'"
            @change="$emit('update:mandalaForm', 'dots')"
            name="btnradio"
            autocomplete="off"
          />
          <label
            for="btnradio3"
            class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 cursor-pointer"
            :class="mandalaForm === 'dots' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-200'"
          >
            {{ $t("message.dotsCaption") }}
          </label>
        </div>
      </div>
    </div>

    <!-- Action buttons -->
    <div
      class="mb-3"
      v-if="b64image != '' && steps[3] && mandalaForm == 'dots'"
    >
      <div class="mb-4">
        <label for="mainMatrix" class="block mb-2 text-sm font-medium text-gray-700">
          {{ $t("message.yourMatrix") }}
        </label>
        <input
          type="text"
          id="mainMatrix"
          :value="pattern"
          @input="$emit('update:pattern', $event.target.value)"
          aria-describedby="wishhelp"
          class="w-full px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
    </div>


    <!-- Action Buttons Section -->
    <div class="flex flex-col sm:flex-row gap-3 mt-6">
      <button
        v-if="steps[2]"
        type="button"
        class="flex-1 px-6 py-3 text-base font-semibold text-white bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-4 focus:ring-purple-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
        @click="$emit('process-request')"
        :disabled="!firstMethodSelected || (remainingMandalaDrawings < 1 && !isPreview)"
        :title="remainingMandalaDrawings < 1 && !isPreview ? $t('message.generateDisabled') : ''"
      >
        {{ remainingMandalaDrawings < 1 && !isPreview ? $t("message.insufficientBalance") : $t("message.go") }}
      </button>
      <button
        v-if="b64image && steps[3]"
        type="button"
        class="flex-1 px-6 py-3 text-base font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-4 focus:ring-gray-200 transition-all duration-200 shadow-sm"
        @click="$emit('open-print-modal')"
      >
        {{ $t("message.print") }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Step3_Customization',
  props: {
    steps: Object,
    patternsList: Array,
    patternLenSelected: Number,
    patternSymbols: String,
    showSectorLength: Boolean,
    sectorLength: Number,
    paletteSelected: Number,
    paletteRendered: Object,
    mandalaForm: String,
    b64image: String,
    pattern: String,
    isPreview: Boolean,
    firstMethodSelected: String,
    remainingMandalaDrawings: Number
  },
  methods: {
    isColorDark(hexColor) {
      if (!hexColor || !hexColor.startsWith('#') || hexColor.length !== 7) {
        return false;
      }

      // Convert hex to RGB
      const r = parseInt(hexColor.slice(1, 3), 16);
      const g = parseInt(hexColor.slice(3, 5), 16);
      const b = parseInt(hexColor.slice(5, 7), 16);

      // Calculate luminance
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

      // Return true if color is dark (luminance < 0.5)
      return luminance < 0.5;
    }
  }
}
</script>
